import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  RadioGroup,
  Radio,
  FormControlLabel
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Star as StarIcon,
  AccountBalance as AccountBalanceIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Layout from '../components/Layout';
import api from '../services/api';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
  PaymentElement
} from '@stripe/react-stripe-js';

// Initialize Stripe
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY 
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) 
  : null;

const PageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
  backgroundColor: '#F7FAFC',
}));

const MainContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  marginLeft: 280,
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(2),
}));

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AddPaymentMethodForm: React.FC<{ 
  companyId: string, 
  clientSecret: string,
  onSuccess: () => void, 
  onCancel: () => void 
}> = ({ companyId, clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) return;

    setLoading(true);
    setError(null);

    try {
      // Confirm the SetupIntent with the PaymentElement
      const result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/companies/${companyId}?tab=billing&setup_success=true`,
        },
        redirect: 'if_required'
      });

      if (result.error) {
        setError(result.error.message || 'Setup failed');
      } else {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        <PaymentElement options={{
          layout: 'tabs',
          wallets: {
            applePay: 'auto',
            googlePay: 'auto'
          }
        }} />
      </Box>
      {error && <Typography color="error" variant="caption" sx={{ mb: 2, display: 'block' }}>{error}</Typography>}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={!stripe || loading}>
          {loading ? <CircularProgress size={24} /> : 'Save Payment Method'}
        </Button>
      </Box>
    </form>
  );
};

const CompanyManagementPageContent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [paymentHistory, setPaymentHistory] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Modal States
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [setupPlanModalOpen, setSetupPlanModalOpen] = useState(false);
  const [adjustLimitsModalOpen, setAdjustLimitsModalOpen] = useState(false);
  const [addPaymentMethodModalOpen, setAddPaymentMethodModalOpen] = useState(false);
  const [setupIntentSecret, setSetupIntentSecret] = useState<string | null>(null);
  const [loadingSetupIntent, setLoadingSetupIntent] = useState(false);
  
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [setupPlanStep, setSetupPlanStep] = useState(1);
  
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDesc, setCreditDesc] = useState('Admin adjustment');
  const [trialDays, setTrialDays] = useState('');
  
  const [setupPlanForm, setSetupPlanForm] = useState({
    plan: 'Essential',
    interval: 'month',
    unit_count: 0,
    additional_users: 0,
    additional_storage_gb: 0,
    trial_days: 0,
    charge_immediately: true,
    promo_code: ''
  });

  const [adjustmentForm, setAdjustmentForm] = useState({
    limit_type: 'units',
    adjustment_amount: 1,
    is_free: false,
    reason: 'Customer request'
  });

  // User Edit State
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [userForm, setUserForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: '',
    status: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [companyData, usersData, tenantsData, logsData, pmData, historyData] = await Promise.all([
        api.getProptrakCompany(id),
        api.request<any[]>(`/api/proptrak/users?company_id=${id}`),
        api.getCompanyTenants(id),
        api.getCompanyAuditLogs(id),
        api.getProptrakPaymentMethods(id),
        api.getProptrakPaymentHistory(id!)
      ]);

      // Handle user data structure variations
      const companyUsers = Array.isArray(usersData) 
        ? usersData.filter((u: any) => u.company_id === id)
        : (usersData as any).users?.filter((u: any) => u.company_id === id) || [];

      setCompany(companyData);
      setUsers(companyUsers);
      setTenants(tenantsData);
      setAuditLogs(logsData || []);
      setPaymentMethods(pmData || []);
      setPaymentHistory(historyData || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load company data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSaveCompany = async () => {
    try {
      await api.updateProptrakCompany(id!, company);
      setSuccess('Company details updated successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to update company');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel this subscription at the end of the period?')) return;
    try {
      await api.cancelCompanySubscription(id!);
      setSuccess('Subscription scheduled for cancellation');
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddCredits = async () => {
    try {
      await api.addCompanyCredits(id!, parseFloat(creditAmount), creditDesc);
      setSuccess('Credits added successfully');
      setCreditModalOpen(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleExtendTrial = async () => {
    if (!company.stripe_subscription) {
      setError('Cannot extend trial: No active subscription found.');
      return;
    }
    
    // Check if we are extending by days or setting specific date
    // Backend supports 'extend_days' or 'end_date'
    // Let's assume user input `trialDays` can be a number (days) or a date string.
    // Simple heuristic: if it contains '-', assume date.
    
    let extendDays: number | undefined;
    let endDate: string | undefined;
    
    if (trialDays.includes('-')) {
        endDate = trialDays;
    } else {
        extendDays = parseInt(trialDays);
    }

    try {
      await api.manageCompanyTrial(id!, extendDays, endDate);
      setSuccess('Trial extended successfully');
      setTrialModalOpen(false);
      loadData(); // Refresh to see new date
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleChangeBillingCycle = async (interval: 'month' | 'year') => {
    if (!window.confirm(`Switch billing cycle to ${interval}? This may result in immediate charges or proration. \n\nWARNING: Active promo chains (Daisy Chains) do NOT carry over automatically on plan changes. We recommend contacting support before proceeding.`)) return;
    try {
      // TODO: Future: Add a "Send Support Ticket" button here for promo chain issues.
      await api.changeCompanyBillingCycle(id!, interval);
      setSuccess(`Billing cycle switched to ${interval}`);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEditUser = (user: any) => {
    setEditingUser(user);
    setUserForm({
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      role: user.role,
      status: user.status
    });
    setUserModalOpen(true);
  };

  const handleSaveUser = async () => {
    try {
      await api.updateUser(editingUser.id, userForm);
      setSuccess('User updated successfully');
      setUserModalOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSuspendUser = async (userId: string, currentStatus: string) => {
    try {
      if (currentStatus === 'active') {
        await api.request(`/api/users/${userId}/suspend`, { method: 'POST' });
      } else {
        await api.request(`/api/users/${userId}/activate`, { method: 'POST' });
      }
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handlePasswordReset = async (email: string) => {
    if (!window.confirm(`Send password reset email to ${email}?`)) return;
    try {
      await api.request('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email })
      });
      setSuccess('Password reset email sent');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleResendInvite = async (userId: string) => {
    try {
      await api.request(`/api/users/${userId}/resend-invitation`, { method: 'POST' });
      setSuccess('Invitation email resent successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to resend invitation');
    }
  };

  const handleSetDefaultPaymentMethod = async (pmId: string) => {
    try {
      await api.setProptrakDefaultPaymentMethod(id!, pmId);
      setSuccess('Default payment method updated');
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeletePaymentMethod = async (pmId: string) => {
    if (!window.confirm('Are you sure you want to remove this payment method?')) return;
    try {
      await api.deleteProptrakPaymentMethod(id!, pmId);
      setSuccess('Payment method removed');
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };
  const handleToggleSuspend = async () => {
    try {
      if (company.is_suspended) {
        await api.activateProptrakCompany(id!);
        setSuccess('Account activated successfully');
      } else {
        if (!window.confirm('Are you sure you want to suspend this account? The PM and all users will lose access.')) return;
        await api.suspendProptrakCompany(id!);
        setSuccess('Account suspended successfully');
      }
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getPlanPrice = () => {
    const prices: any = {
      'Essential': { month: 75, year: 840, addon: 0, included: 20 },
      'Pro': { month: 150, year: 1680, addon: 5, addonYear: 54, included: 50 },
      'Enterprise': { month: 500, year: 5400, addon: 4, addonYear: 42, included: 150 }
    };
    const config = prices[setupPlanForm.plan];
    const base = config[setupPlanForm.interval];
    const additional = Math.max(0, setupPlanForm.unit_count - config.included);
    const addonPrice = setupPlanForm.interval === 'month' ? config.addon : (config.addonYear || config.addon * 12);
    
    // User pricing
    const userPrice = setupPlanForm.interval === 'month' ? 10 : 100;
    const userCost = setupPlanForm.additional_users * userPrice;
    
    // Storage pricing ($10/50GB/mo)
    const storagePrice = setupPlanForm.interval === 'month' ? 10 : 100;
    const storageCost = (setupPlanForm.additional_storage_gb / 50) * storagePrice;
    
    return {
        base,
        additional: additional * addonPrice,
        users_cost: userCost,
        storage_cost: storageCost,
        total: base + (additional * addonPrice) + userCost + storageCost,
        unit_count: setupPlanForm.unit_count,
        included: config.included,
        addon_count: additional,
        user_count: setupPlanForm.additional_users,
        storage_gb: setupPlanForm.additional_storage_gb
    };
  };

  const handleSetupPlan = async () => {
    if (setupPlanStep === 1) {
      if (setupPlanForm.plan === 'Essential' && setupPlanForm.unit_count > 20) {
        setError('Essential plan is limited to 20 units maximum.');
        return;
      }
      setSetupPlanStep(2);
      return;
    }

    if (!selectedPaymentMethod && setupPlanForm.charge_immediately) {
      setError('Please select a payment method');
      return;
    }

    try {
      const trialDays = setupPlanForm.charge_immediately ? 0 : setupPlanForm.trial_days;
      await api.createProptrakSubscription(
        id!, 
        setupPlanForm.plan, 
        setupPlanForm.interval, 
        selectedPaymentMethod, 
        trialDays, 
        setupPlanForm.unit_count,
        setupPlanForm.additional_users,
        setupPlanForm.additional_storage_gb,
        setupPlanForm.promo_code
      );
      setSuccess(`Plan ${setupPlanForm.plan} set up successfully`);
      setSetupPlanModalOpen(false);
      setSetupPlanStep(1);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const openSetupPlanModal = () => {
    setSetupPlanForm({
      plan: company.subscription_plan || 'Essential',
      interval: company.billing_cycle === 'yearly' ? 'year' : 'month',
      unit_count: company.current_unit_count || 0,
      additional_users: 0,
      additional_storage_gb: 0,
      trial_days: 0,
      charge_immediately: true,
      promo_code: ''
    });
    setSetupPlanModalOpen(true);
  };

  const handleAdjustLimits = async () => {
    try {
      await api.adjustProptrakLimits(id!, adjustmentForm);
      setSuccess(`${adjustmentForm.limit_type} adjusted successfully`);
      setAdjustLimitsModalOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const openAddPaymentMethodModal = async () => {
    try {
      if (!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
        throw new Error('Stripe Publishable Key is not configured. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in the environment.');
      }
      setLoadingSetupIntent(true);
      setAddPaymentMethodModalOpen(true);
      const { client_secret } = await api.createProptrakSetupIntent(id!);
      setSetupIntentSecret(client_secret);
    } catch (err: any) {
      setError('Failed to initialize payment method setup: ' + err.message);
      setAddPaymentMethodModalOpen(false);
    } finally {
      setLoadingSetupIntent(false);
    }
  };
  const openAdjustLimitsModal = (type: string = 'units') => {
    setAdjustmentForm({
      ...adjustmentForm,
      limit_type: type,
      adjustment_amount: type === 'storage' ? 50 : 1
    });
    setAdjustLimitsModalOpen(true);
  };

  if (loading) {
    return (
      <PageContainer>
        <Layout>
          <MainContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
              <CircularProgress />
            </Box>
          </MainContent>
        </Layout>
      </PageContainer>
    );
  }

  if (!company) return null;

  return (
    <PageContainer>
      <Layout>
        <MainContent>
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="Back to Companies">
              <IconButton onClick={() => navigate('/companies')}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="h4" fontWeight="600">
              {company.company_name}
            </Typography>
            <Chip 
              label={company.subscription_plan} 
              color={company.subscription_plan === 'Enterprise' ? 'primary' : 'default'} 
              sx={{ ml: 2 }}
            />
          </Box>

          <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tab icon={<BusinessIcon />} iconPosition="start" label="Company Details" />
            <Tab icon={<PersonIcon />} iconPosition="start" label="Users" />
            <Tab icon={<PersonIcon />} iconPosition="start" label="Tenants" />
            <Tab icon={<CreditCardIcon />} iconPosition="start" label="Billing & Plan" />
            <Tab icon={<CreditCardIcon />} iconPosition="start" label="Audit Trail" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <StyledCard>
              <CardContent>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField 
                        fullWidth label="Company Name" 
                        value={company.company_name} 
                        onChange={(e) => setCompany({...company, company_name: e.target.value})}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControl fullWidth>
                        <InputLabel>Entity Type</InputLabel>
                        <Select
                          value={company.entity_type || ''}
                          label="Entity Type"
                          onChange={(e) => setCompany({...company, entity_type: e.target.value})}
                        >
                          <MenuItem value="LLC">LLC</MenuItem>
                          <MenuItem value="Corporation">Corporation</MenuItem>
                          <MenuItem value="Sole Proprietorship">Sole Proprietorship</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField 
                        fullWidth label="Address Line 1" 
                        value={company.address_line_1 || ''} 
                        onChange={(e) => setCompany({...company, address_line_1: e.target.value})}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField 
                        fullWidth label="City" 
                        value={company.city || ''} 
                        onChange={(e) => setCompany({...company, city: e.target.value})}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField 
                        fullWidth label="State" 
                        value={company.state || ''} 
                        onChange={(e) => setCompany({...company, state: e.target.value})}
                      />
                    </Grid>
                    <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
                      <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveCompany}>
                        Save Changes
                      </Button>
                    </Grid>
                  </Grid>
              </CardContent>
            </StyledCard>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <StyledCard>
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            {user.first_name} {user.last_name}
                            {(user.is_primary_user || user.id === company.user_id) && (
                              <Chip 
                                icon={<StarIcon style={{ color: '#FFD700' }} />} 
                                label="Primary" 
                                size="small" 
                                sx={{ ml: 1, bgcolor: 'rgba(255, 215, 0, 0.1)', border: '1px solid #FFD700' }} 
                              />
                            )}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={user.role} size="small" color={(user.is_primary_user || user.id === company.user_id) ? "primary" : "default"} variant={(user.is_primary_user || user.id === company.user_id) ? "filled" : "outlined"} />
                              {(user.is_primary_user || user.id === company.user_id) && (
                                <Tooltip title="Primary User - Role cannot be changed">
                                  <StarIcon sx={{ fontSize: 18, color: '#FFD700' }} />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={user.status} 
                              color={user.status === 'active' ? 'success' : 'error'} 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Edit User">
                              <IconButton size="small" onClick={() => handleEditUser(user)}><EditIcon /></IconButton>
                            </Tooltip>
                            <Tooltip title={(user.is_primary_user || user.id === company.user_id) ? "Primary user cannot be suspended" : (user.status === 'active' ? "Suspend User" : "Activate User")}>
                              <span>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleSuspendUser(user.id, user.status)}
                                  disabled={user.is_primary_user || user.id === company.user_id}
                                >
                                  {user.status === 'active' ? <BlockIcon color={(user.is_primary_user || user.id === company.user_id) ? "disabled" : "error"} /> : <CheckCircleIcon color="success" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            {user.status === 'invited' || user.status === 'pending' ? (
                              <Button 
                                size="small" 
                                startIcon={<EmailIcon />}
                                onClick={() => handleResendInvite(user.id)}
                              >
                                Resend Invite
                              </Button>
                            ) : (
                              <Button size="small" onClick={() => handlePasswordReset(user.email)}>Reset Pwd</Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </StyledCard>
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            <StyledCard>
              <CardContent>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Property</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tenants.map((tenant) => (
                        <TableRow key={tenant.id}>
                          <TableCell>{tenant.first_name} {tenant.last_name}</TableCell>
                          <TableCell>{tenant.email}</TableCell>
                          <TableCell>{tenant.property_name || 'N/A'}</TableCell>
                          <TableCell>{tenant.unit_number}</TableCell>
                          <TableCell>
                            <Chip 
                                label={tenant.user_status} 
                                color={tenant.user_status === 'active' ? 'success' : (tenant.user_status === 'invited' || tenant.user_status === 'pending' ? 'info' : 'default')} 
                                size="small" 
                            />
                          </TableCell>
                          <TableCell>
                            {tenant.user_status === 'active' ? (
                                <Button size="small" onClick={() => handlePasswordReset(tenant.email)}>Reset Pwd</Button>
                            ) : (
                                <Button 
                                    size="small" 
                                    startIcon={<EmailIcon />}
                                    onClick={() => handleResendInvite(tenant.user_id)}
                                    disabled={!tenant.user_id}
                                >
                                    Resend Invite
                                </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </StyledCard>
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <StyledCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Account Status</Typography>
                      <Chip 
                        label={company.is_suspended ? "Suspended" : "Active"} 
                        color={company.is_suspended ? "error" : "success"}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {company.is_suspended 
                        ? "This account is currently suspended. Users cannot log in or perform actions." 
                        : "This account is active and users can log in."}
                    </Typography>
                    <Button 
                      variant="outlined" 
                      color={company.is_suspended ? "success" : "error"} 
                      onClick={handleToggleSuspend}
                    >
                      {company.is_suspended ? "Activate Account" : "Suspend Account"}
                    </Button>
                  </CardContent>
                </StyledCard>

                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Subscription Status</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">Current Plan</Typography>
                      <Typography variant="h5">
                        {company.subscription_plan} ({company.billing_cycle || 'monthly'}) - ${(company.stripe_subscription?.total_amount || 0) / 100}/{company.billing_cycle === 'year' ? 'yr' : 'mo'}
                      </Typography>
                      {company.stripe_subscription?.status === 'trialing' && (
                        <Typography variant="caption" color="text.secondary">
                          * Amount to be paid when trial ends
                        </Typography>
                      )}
                    </Box>
                    
                    {company.stripe_subscription ? (
                      <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                        <Grid container spacing={1}>
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="caption" color="text.secondary">Stripe Status</Typography>
                            <Typography variant="body2" fontWeight="600" sx={{ textTransform: 'capitalize' }}>
                              {company.stripe_subscription.status}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 6 }}>
                            <Typography variant="caption" color="text.secondary">Period Ends</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {company.stripe_subscription.current_period_end ? new Date(company.stripe_subscription.current_period_end).toLocaleDateString() : 'N/A'}
                            </Typography>
                          </Grid>
                          {company.stripe_subscription.cancel_at_period_end && (
                            <Grid size={{ xs: 12 }}>
                              <Alert severity="warning" sx={{ mt: 1 }}>
                                Scheduled for cancellation at period end.
                              </Alert>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    ) : (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        No active Stripe subscription found.
                      </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {company.stripe_subscription && (
                        <>
                          <Button variant="outlined" color="error" onClick={handleCancelSubscription}>
                            Cancel Subscription
                          </Button>
                          <Button variant="outlined" onClick={() => handleChangeBillingCycle(company.billing_cycle === 'monthly' ? 'year' : 'month')}>
                            Switch to {company.billing_cycle === 'monthly' ? 'Yearly' : 'Monthly'}
                          </Button>
                        </>
                      )}
                      {!company.stripe_subscription && (
                        <Button variant="contained" color="primary" onClick={openSetupPlanModal}>
                          Set Up Plan
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <StyledCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Usage Limits</Typography>
                      <Button size="small" startIcon={<EditIcon />} onClick={() => openAdjustLimitsModal('units')}>Adjust</Button>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">Included Units</Typography>
                        <Typography variant="body1" fontWeight="600">{company.included_units || 0}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">Purchased Units</Typography>
                        <Typography variant="body1" fontWeight="600">{company.additional_units_purchased || 0}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">Included Users</Typography>
                        <Typography variant="body1" fontWeight="600">
                          {company.included_users === null ? 'Unlimited' : (company.included_users || 'Default')}
                        </Typography>
                      </Grid>
                      {company.subscription_plan !== 'Enterprise' && (
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">Purchased Users</Typography>
                          <Typography variant="body1" fontWeight="600">{company.additional_users_purchased || 0}</Typography>
                        </Grid>
                      )}
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">Hard Unit Limit</Typography>
                        <Typography variant="body1" fontWeight="600">{company.max_units_allowed || 'Unlimited'}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">Storage Limit</Typography>
                        <Typography variant="body1" fontWeight="600">
                          {company.storage_limit_bytes ? `${(company.storage_limit_bytes / (1024 * 1024 * 1024)).toFixed(1)} GB` : 'Unlimited'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                         <Box sx={{ mt: 1, p: 1, border: '1px dashed #ddd', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">Current Total Usage</Typography>
                            <Grid container>
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="body2">
                                        <b>Units:</b> {company.current_unit_count || 0} / {(company.included_units || 0) + (company.additional_units_purchased || 0)}
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="body2">
                                        <b>Users:</b> {company.current_user_count || 0} / {
                                            company.included_users === null 
                                                ? 'Unlimited' 
                                                : (company.included_users || 0) + (company.additional_users_purchased || 0)
                                        }
                                    </Typography>
                                </Grid>
                                <Grid size={{ xs: 4 }}>
                                    <Typography variant="body2">
                                        <b>Storage:</b> {(company.storage_used_bytes || 0) / (1024 * 1024) > 1024 
                                            ? `${((company.storage_used_bytes || 0) / (1024 * 1024 * 1024)).toFixed(2)} GB` 
                                            : `${((company.storage_used_bytes || 0) / (1024 * 1024)).toFixed(2)} MB`}
                                    </Typography>
                                </Grid>
                            </Grid>
                         </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>

                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Adjustments</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                      <Button variant="contained" onClick={() => setCreditModalOpen(true)}>
                        Add Credits
                      </Button>
                      <span title={(!company.stripe_subscription || (company.stripe_subscription.status !== 'active' && company.stripe_subscription.status !== 'trialing')) ? "Requires active subscription" : ""}>
                        <Button 
                          variant="contained" 
                          color="secondary" 
                          onClick={() => setTrialModalOpen(true)}
                          disabled={!company.stripe_subscription || (company.stripe_subscription.status !== 'active' && company.stripe_subscription.status !== 'trialing')}
                        >
                          Manage Trial
                        </Button>
                      </span>
                    </Box>
                  </CardContent>
                </StyledCard>
                <StyledCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">Payment Methods</Typography>
                      <Button size="small" startIcon={<AddIcon />} onClick={openAddPaymentMethodModal}>Add New</Button>
                    </Box>
                    
                    {paymentMethods.length > 0 ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Method</TableCell>
                              <TableCell>Default</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {paymentMethods.map((pm) => (
                              <TableRow key={pm.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {pm.type === 'card' ? <CreditCardIcon fontSize="small" /> : <AccountBalanceIcon fontSize="small" />}
                                    <Typography variant="body2">
                                      {pm.brand ? `${pm.brand.toUpperCase()} ****${pm.last4}` : `${pm.bank_name || 'Bank'} ****${pm.last4}`}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {pm.is_default ? (
                                    <Chip label="Default" size="small" color="primary" />
                                  ) : (
                                    <Button size="small" onClick={() => handleSetDefaultPaymentMethod(pm.id)}>Set Default</Button>
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton size="small" color="error" onClick={() => handleDeletePaymentMethod(pm.id)} disabled={pm.is_default}>
                                    <DeleteIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No payment methods saved.</Typography>
                    )}
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Payment History</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Invoice #</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paymentHistory.length > 0 ? (
                            paymentHistory.map((inv) => (
                              <TableRow key={inv.id}>
                                <TableCell>{new Date(inv.date).toLocaleDateString()}</TableCell>
                                <TableCell>{inv.number}</TableCell>
                                <TableCell>${inv.amount.toFixed(2)}</TableCell>
                                <TableCell>
                                  <Chip 
                                    label={inv.status.toUpperCase()} 
                                    size="small" 
                                    color={inv.status === 'paid' ? 'success' : inv.status === 'open' ? 'warning' : 'default'}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  {inv.pdf_url && (
                                    <Button 
                                      size="small" 
                                      href={inv.pdf_url} 
                                      target="_blank"
                                      startIcon={<LinkIcon />}
                                    >
                                      PDF
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} align="center">No payment history found.</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={activeTab} index={4}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>Usage Limit Adjustments</Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Cost</TableCell>
                        <TableCell>Performed By</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>Source</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditLogs.length > 0 ? (
                        auditLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell>{new Date(log.created_at).toLocaleString()}</TableCell>
                            <TableCell style={{ textTransform: 'capitalize' }}>{log.limit_type}</TableCell>
                            <TableCell>
                                {log.limit_type === 'storage' 
                                    ? `+${(log.adjustment_amount / (1024*1024*1024)).toFixed(1)} GB` 
                                    : `+${log.adjustment_amount}`}
                            </TableCell>
                            <TableCell>
                                {log.is_free ? (
                                    <Chip label="Free" color="success" size="small" variant="outlined" />
                                ) : (
                                    `$${log.cost_amount?.toFixed(2)}`
                                )}
                            </TableCell>
                            <TableCell>{log.performed_by || 'System'}</TableCell>
                            <TableCell>{log.reason}</TableCell>
                            <TableCell>
                                <Chip 
                                    label={log.source === 'aletheia' ? 'Admin Portal' : 'PropTrak'} 
                                    size="small" 
                                    color={log.source === 'aletheia' ? 'primary' : 'default'}
                                />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">No audit logs found.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </StyledCard>
          </TabPanel>

          <Dialog open={creditModalOpen} onClose={() => setCreditModalOpen(false)}>
            <DialogTitle>Add Credits</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus margin="dense" label="Amount ($)" type="number" fullWidth
                value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)}
              />
              <TextField
                margin="dense" label="Description" fullWidth
                value={creditDesc} onChange={(e) => setCreditDesc(e.target.value)}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCreditModalOpen(false)}>Cancel</Button>
              <Button onClick={handleAddCredits} variant="contained">Add Credit</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={trialModalOpen} onClose={() => setTrialModalOpen(false)}>
            <DialogTitle>Manage Trial</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
                Update the trial period for this account. You can enter a number of days to add (e.g., "30") or a specific end date (e.g., "2026-12-31").
              </Typography>
              <TextField
                autoFocus margin="dense" label="Days to add or End Date (YYYY-MM-DD)" fullWidth
                value={trialDays} onChange={(e) => setTrialDays(e.target.value)}
                helperText="Providing a number adds days; providing a date sets a fixed expiration."
              />
              {company.stripe_subscription?.status === 'trialing' && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Current Trial Ends:</Typography>
                  <Typography variant="body2" fontWeight="600">
                    {company.stripe_subscription.current_period_end ? new Date(company.stripe_subscription.current_period_end).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setTrialModalOpen(false)}>Cancel</Button>
              <Button onClick={handleExtendTrial} variant="contained" color="secondary">Update Trial</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={userModalOpen} onClose={() => setUserModalOpen(false)}>
            <DialogTitle>Edit User</DialogTitle>
            <DialogContent>
              <TextField
                margin="dense" label="First Name" fullWidth
                value={userForm.first_name} onChange={(e) => setUserForm({...userForm, first_name: e.target.value})}
              />
              <TextField
                margin="dense" label="Last Name" fullWidth
                value={userForm.last_name} onChange={(e) => setUserForm({...userForm, last_name: e.target.value})}
              />
              <TextField
                margin="dense" label="Email" fullWidth
                value={userForm.email} onChange={(e) => setUserForm({...userForm, email: e.target.value})}
              />
              <FormControl fullWidth margin="dense">
                <InputLabel>Role</InputLabel>
                <Select
                  value={userForm.role}
                  label="Role"
                  onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                  disabled={editingUser?.is_primary_user || editingUser?.id === company.user_id}
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="property_manager">Property Manager</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                </Select>
              </FormControl>
              {(editingUser?.is_primary_user || editingUser?.id === company.user_id) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Primary user role cannot be changed.
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setUserModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveUser} variant="contained">Save</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={adjustLimitsModalOpen} onClose={() => setAdjustLimitsModalOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Adjust Usage Limits</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, mt: 1 }}>
                Incremental addition to current limits. This will record an audit trail in both Aletheia and PropTrak.
              </Typography>
              <Grid container spacing={3}>
                <Grid size={{ xs: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Adjustment Type</InputLabel>
                    <Select
                      value={adjustmentForm.limit_type}
                      label="Adjustment Type"
                      onChange={(e) => setAdjustmentForm({...adjustmentForm, limit_type: e.target.value})}
                    >
                      <MenuItem value="units">Additional Units</MenuItem>
                      {company.subscription_plan !== 'Enterprise' && (
                        <MenuItem value="users">Additional Users</MenuItem>
                      )}
                      <MenuItem value="storage">Additional Storage (GB)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  {adjustmentForm.limit_type === 'storage' ? (
                    <FormControl fullWidth>
                      <InputLabel>Amount to Add (GB)</InputLabel>
                      <Select
                        value={adjustmentForm.adjustment_amount}
                        label="Amount to Add (GB)"
                        onChange={(e) => setAdjustmentForm({...adjustmentForm, adjustment_amount: typeof e.target.value === 'string' ? parseFloat(e.target.value) : e.target.value as number})}
                      >
                        {[50, 100, 150, 200, 250, 500, 1000].map(val => (
                          <MenuItem key={val} value={val}>{val} GB</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      label="Amount to Add" type="number" fullWidth
                      value={adjustmentForm.adjustment_amount} 
                      onChange={(e) => setAdjustmentForm({...adjustmentForm, adjustment_amount: parseFloat(e.target.value)})}
                    />
                  )}
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="Reason for Adjustment" fullWidth multiline rows={2}
                    value={adjustmentForm.reason} 
                    onChange={(e) => setAdjustmentForm({...adjustmentForm, reason: e.target.value})}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="subtitle2">Charge Customer via Stripe</Typography>
                      <input 
                        type="checkbox" 
                        checked={!adjustmentForm.is_free} 
                        onChange={(e) => setAdjustmentForm({...adjustmentForm, is_free: !e.target.checked})}
                        style={{ width: 20, height: 20 }}
                      />
                    </Box>
                    {!adjustmentForm.is_free && (
                        <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                            Estimated Prorated Charge: ${
                                adjustmentForm.limit_type === 'storage' 
                                    ? (0.2 * adjustmentForm.adjustment_amount).toFixed(2) // 50GB = $10 ($0.20/GB)
                                    : ((adjustmentForm.limit_type === 'units' ? 5 : 10) * adjustmentForm.adjustment_amount * 0.5).toFixed(2) // Using simplified 0.5 ratio
                            } (Reflects current billing period remaining days)
                        </Typography>
                    )}
                    {adjustmentForm.is_free && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                            No charge will be applied to the customer's Stripe account.
                        </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAdjustLimitsModalOpen(false)}>Cancel</Button>
              <Button onClick={handleAdjustLimits} variant="contained" color="primary">Apply Adjustment</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={setupPlanModalOpen} onClose={() => { setSetupPlanModalOpen(false); setSetupPlanStep(1); }} maxWidth="sm" fullWidth>
            <DialogTitle>Set Up Plan - Step {setupPlanStep} of 2</DialogTitle>
            <DialogContent>
              {setupPlanStep === 1 ? (
                <>
                  <Typography variant="body2" sx={{ mb: 3, mt: 1 }}>
                    Select a tier and billing cycle to create a new Stripe subscription for this company.
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Tier</InputLabel>
                    <Select
                      value={setupPlanForm.plan}
                      label="Tier"
                      onChange={(e) => setSetupPlanForm({...setupPlanForm, plan: e.target.value})}
                    >
                      <MenuItem value="Essential">Essential</MenuItem>
                      <MenuItem value="Pro">Pro</MenuItem>
                      <MenuItem value="Enterprise">Enterprise</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Billing Cycle</InputLabel>
                    <Select
                      value={setupPlanForm.interval}
                      label="Billing Cycle"
                      onChange={(e) => setSetupPlanForm({...setupPlanForm, interval: e.target.value})}
                    >
                      <MenuItem value="month">Monthly</MenuItem>
                      <MenuItem value="year">Yearly</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Unit Count"
                    type="number"
                    fullWidth
                    sx={{ mb: 3 }}
                    value={setupPlanForm.unit_count}
                    onChange={(e) => setSetupPlanForm({...setupPlanForm, unit_count: parseInt(e.target.value) || 0})}
                    helperText={`Included: ${setupPlanForm.plan === 'Essential' ? 20 : setupPlanForm.plan === 'Pro' ? 50 : 150} units. Extra units will be charged accordingly.`}
                  />

                  {setupPlanForm.plan !== 'Enterprise' && (
                    <>
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 6 }}>
                          <TextField
                            label="Additional Users"
                            type="number"
                            fullWidth
                            sx={{ mb: 3 }}
                            value={setupPlanForm.additional_users}
                            onChange={(e) => setSetupPlanForm({...setupPlanForm, additional_users: parseInt(e.target.value) || 0})}
                            helperText="$10/mo per extra user"
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Additional Storage</InputLabel>
                            <Select
                              value={setupPlanForm.additional_storage_gb}
                              label="Additional Storage"
                              onChange={(e) => setSetupPlanForm({...setupPlanForm, additional_storage_gb: Number(e.target.value)})}
                            >
                              <MenuItem value={0}>None</MenuItem>
                              {[50, 100, 150, 200, 250, 500, 1000].map(val => (
                                <MenuItem key={val} value={val}>{val} GB (+$10/mo)</MenuItem>
                              ))}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5, mt: 0.5 }}>
                              $10/50GB per month
                            </Typography>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </>
                  )}

                  <TextField
                    label="Promo Code"
                    fullWidth
                    sx={{ mb: 3 }}
                    value={setupPlanForm.promo_code}
                    onChange={(e) => setSetupPlanForm({...setupPlanForm, promo_code: e.target.value})}
                    placeholder="Enter promo code (optional)"
                    helperText="Apply a promo chain discount to this subscription"
                  />

                  <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Billing Options</Typography>
                    <RadioGroup
                      value={setupPlanForm.charge_immediately ? 'charge' : 'trial'}
                      onChange={(e) => setSetupPlanForm({...setupPlanForm, charge_immediately: e.target.value === 'charge'})}
                    >
                      <FormControlLabel value="charge" control={<Radio size="small" />} label={<Typography variant="body2">Charge Now</Typography>} />
                      <FormControlLabel value="trial" control={<Radio size="small" />} label={<Typography variant="body2">Begin Trial</Typography>} />
                    </RadioGroup>
                    
                    {!setupPlanForm.charge_immediately && (
                        <Box sx={{ mt: 2 }}>
                            <TextField
                                label="Trial Period (Days)"
                                type="number"
                                fullWidth
                                size="small"
                                value={setupPlanForm.trial_days}
                                onChange={(e) => setSetupPlanForm({...setupPlanForm, trial_days: parseInt(e.target.value) || 0})}
                                helperText="The first payment will be delayed by this many days."
                            />
                        </Box>
                    )}
                  </Box>
                  
                  <Box sx={{ p: 2, bgcolor: 'rgba(107, 70, 193, 0.05)', borderRadius: 2, border: '1px solid rgba(107, 70, 193, 0.1)' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>Price Breakdown</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{setupPlanForm.plan} Plan ({setupPlanForm.interval === 'month' ? 'Monthly' : 'Annual'})</Typography>
                        <Typography variant="body2" fontWeight="600">${getPlanPrice().base.toFixed(2)}</Typography>
                    </Box>
                    {getPlanPrice().addon_count > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Additional Units (+{getPlanPrice().addon_count})</Typography>
                            <Typography variant="body2" fontWeight="600">${getPlanPrice().additional.toFixed(2)}</Typography>
                        </Box>
                    )}
                    {getPlanPrice().user_count > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Additional Users (+{getPlanPrice().user_count})</Typography>
                            <Typography variant="body2" fontWeight="600">${getPlanPrice().users_cost.toFixed(2)}</Typography>
                        </Box>
                    )}
                    {getPlanPrice().storage_gb > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Additional Storage (+{getPlanPrice().storage_gb}GB)</Typography>
                            <Typography variant="body2" fontWeight="600">${getPlanPrice().storage_cost.toFixed(2)}</Typography>
                        </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                        <Typography variant="subtitle2">Total Amount</Typography>
                        <Typography variant="subtitle2" fontWeight="700">${getPlanPrice().total.toFixed(2)}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        * Plus applicable taxes. Subscriptions auto-renew.
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mb: 3, mt: 1 }}>
                    Select a saved payment method or add a new one for this subscription.
                  </Typography>
                  
                  <FormControl fullWidth sx={{ mb: 3 }}>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={selectedPaymentMethod}
                      label="Payment Method"
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                      renderValue={(selected) => {
                        const pm = paymentMethods.find(p => p.id === selected);
                        if (!pm) return selected;
                        return pm.brand ? `${pm.brand.toUpperCase()} ****${pm.last4}` : `${pm.bank_name || 'Bank'} ****${pm.last4}`;
                      }}
                    >
                      {paymentMethods.map(pm => (
                        <MenuItem key={pm.id} value={pm.id}>
                          {pm.brand ? `${pm.brand.toUpperCase()} ****${pm.last4}` : `${pm.bank_name || 'Bank'} ****${pm.last4}`} {pm.is_default ? '(Default)' : ''}
                        </MenuItem>
                      ))}
                      <MenuItem value="new" onClick={openAddPaymentMethodModal}>+ Add New Payment Method</MenuItem>
                    </Select>
                  </FormControl>

                  {paymentMethods.length === 0 && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        No saved payment methods found. Please add one first.
                    </Alert>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => { setSetupPlanModalOpen(false); setSetupPlanStep(1); }}>Cancel</Button>
              {setupPlanStep === 2 && (
                <Button onClick={() => setSetupPlanStep(1)}>Back</Button>
              )}
              <Button onClick={handleSetupPlan} variant="contained" color="primary" disabled={setupPlanStep === 2 && !selectedPaymentMethod}>
                {setupPlanStep === 1 ? 'Next' : 'Create Subscription'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={addPaymentMethodModalOpen} onClose={() => setAddPaymentMethodModalOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Add Payment Method</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 3, mt: 1 }}>
                Enter card or bank details below to save a new payment method for this company.
              </Typography>
              {loadingSetupIntent ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (setupIntentSecret && stripePromise) ? (
                <Elements stripe={stripePromise} options={{ clientSecret: setupIntentSecret }}>
                  <AddPaymentMethodForm 
                    companyId={id!} 
                    clientSecret={setupIntentSecret}
                    onSuccess={() => {
                      setAddPaymentMethodModalOpen(false);
                      setSetupIntentSecret(null);
                      setSuccess('Payment method added successfully');
                      loadData();
                    }}
                    onCancel={() => {
                      setAddPaymentMethodModalOpen(false);
                      setSetupIntentSecret(null);
                    }}
                  />
                </Elements>
              ) : (
                <Alert severity="error">Failed to initialize payment setup. Please try again.</Alert>
              )}
            </DialogContent>
          </Dialog>

          <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess(null)}>
            <Alert severity="success">{success}</Alert>
          </Snackbar>
          <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
            <Alert severity="error">{error}</Alert>
          </Snackbar>

        </MainContent>
      </Layout>
    </PageContainer>
  );
};

const CompanyManagementPage: React.FC = () => {
    return stripePromise ? (
        <Elements stripe={stripePromise}>
            <CompanyManagementPageContent />
        </Elements>
    ) : (
        <CompanyManagementPageContent />
    );
};

export default CompanyManagementPage;
